// Not ported (dead in the Angular app): getEvents, deleteSignatory, createSnapshot, rollForward, getRollForwardRuns, assignReviewPoint
import type { OutputProfile } from '@/core/outputProfile';
import { api } from '@/core/http/apiClient';
import { newIdempotencyKey } from '@/core/http/helpers';
import type {
  ApprovalState,
  ClearReadyPayload,
  ClearReadyResult,
  CreateRevisionPayload,
  DocumentVersionRow,
  FinalisationSnapshot,
  FinaliseRevisionPayload,
  FinaliseRevisionResult,
  RaiseReviewPointPayload,
  RecordApprovalPayload,
  RevisionCase,
  ReviewPointDetail,
  SaveSignatoriesPayload,
  SetReadyPayload,
  SignatoriesView,
  ValidationRunSummary,
} from '@/core/models/approval.models';

const idempotent = () => ({ headers: { 'Idempotency-Key': newIdempotencyKey() } });

export const approvalService = {
  // ---- Approval readiness ----

  getState: (periodId: string) => api.get<ApprovalState>(`Approval/getState/${periodId}`),

  setReady: (periodId: string, payload: SetReadyPayload) =>
    api.post<ApprovalState>(`Approval/setReady/${periodId}`, payload),

  clearReady: (periodId: string, payload: ClearReadyPayload) =>
    api.post<ClearReadyResult>(`Approval/clearReady/${periodId}`, payload),

  recordApproval: (periodId: string, payload: RecordApprovalPayload) =>
    api.post<ApprovalState>(`Approval/recordApproval/${periodId}`, payload),

  // ---- Signatories ----

  // revisionCaseId is deliberately not URL-encoded (as in Angular).
  getSignatories: (periodId: string, revisionCaseId?: string | null) => {
    const query = revisionCaseId ? `?revisionCaseId=${revisionCaseId}` : '';
    return api.get<SignatoriesView>(`Approval/getSignatories/${periodId}${query}`);
  },

  saveSignatories: (periodId: string, payload: SaveSignatoriesPayload) =>
    api.post<SignatoriesView>(`Approval/saveSignatories/${periodId}`, payload),

  // ---- Finalisation ----

  getSnapshots: (periodId: string) => api.get<FinalisationSnapshot[]>(`Approval/getSnapshots/${periodId}`),

  // ---- Revised accounts ----

  getRevisions: (periodId: string) => api.get<RevisionCase[]>(`Approval/getRevisions/${periodId}`),

  getPeriodDocuments: (periodId: string, outputProfile?: OutputProfile | null) => {
    const path = `accounts-production/periods/${periodId}/documents`;
    return outputProfile
      ? api.get<DocumentVersionRow[]>(path, { params: { outputProfile } })
      : api.get<DocumentVersionRow[]>(path);
  },

  /** Generates the Full accounts unless `outputProfile` names the Abridged or Filleted ones. */
  generateDocument: (periodId: string, outputProfile?: OutputProfile | null) =>
    api.post<DocumentVersionRow>(
      `accounts-production/periods/${periodId}/documents`,
      outputProfile ? { outputProfile } : {},
      idempotent(),
    ),

  changePeriodStatus: (periodId: string, status: number) =>
    api.post<unknown>(`accounts-production/periods/${periodId}/status`, { status }),

  approveDocument: (periodId: string, documentVersionId: string) =>
    api.post<unknown>(`accounts-production/periods/${periodId}/approvals`, { documentVersionId }, idempotent()),

  finalisePeriod: (periodId: string, documentVersionId: string) =>
    api.post<unknown>(`accounts-production/periods/${periodId}/finalise`, { documentVersionId }, idempotent()),

  /** APT336: finalised accounts back to Approved, with a reason (refused once filed or submitted, or when a revision exists). */
  unfinalisePeriod: (periodId: string, reason: string) =>
    api.post<unknown>(`accounts-production/periods/${periodId}/unfinalise`, { reason }, idempotent()),

  /** Runs the validations on the current figures and stores the run: finalising needs one that is up to date. */
  runValidations: (periodId: string) =>
    api.post<ValidationRunSummary>(`accounts-production/periods/${periodId}/validations`, {}),

  createRevision: (periodId: string, payload: CreateRevisionPayload) =>
    api.post<RevisionCase>(`Approval/createRevision/${periodId}`, payload),

  finaliseRevision: (revisionCaseId: string, payload: FinaliseRevisionPayload) =>
    api.post<FinaliseRevisionResult>(`Approval/finaliseRevision/${revisionCaseId}`, payload),

  abandonRevision: (revisionCaseId: string, reason: string | null) =>
    api.post<RevisionCase>(`Approval/abandonRevision/${revisionCaseId}`, { reason }),

  // ---- Review workflow ----

  getReviewPoints: (periodId: string, status?: string | null) => {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return api.get<ReviewPointDetail[]>(`Approval/getReviewPoints/${periodId}${query}`);
  },

  raiseReviewPoint: (periodId: string, payload: RaiseReviewPointPayload) =>
    api.post<ReviewPointDetail[]>(`Approval/raiseReviewPoint/${periodId}`, payload),

  respondReviewPoint: (periodId: string, reviewPointId: string, responseText: string) =>
    api.post<ReviewPointDetail[]>(`Approval/respondReviewPoint/${periodId}/${reviewPointId}`, { responseText }),

  clearReviewPoint: (periodId: string, reviewPointId: string) =>
    api.post<ReviewPointDetail[]>(`Approval/clearReviewPoint/${periodId}/${reviewPointId}`, {}),
};
